import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Table } from 'react-bootstrap';
import { getDatabase, ref, onValue, push, update, remove } from 'firebase/database';
import { FaEdit, FaTrash, FaUsers } from 'react-icons/fa';

const Batches = () => {
  const [batches, setBatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    course: '',
    startDate: '',
    endDate: '',
    timing: '',
    capacity: '',
    teacher: '',
    status: 'Active',
    description: ''
  });

  useEffect(() => {
    const db = getDatabase();
    const batchesRef = ref(db, 'batches');
    const unsubscribe = onValue(batchesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const batchesList = Object.entries(data).map(([id, batch]) => ({
          id,
          ...batch
        }));
        setBatches(batchesList);
      } else {
        setBatches([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const db = getDatabase();
    
    if (editingBatch) {
      await update(ref(db, `batches/${editingBatch.id}`), formData);
    } else {
      await push(ref(db, 'batches'), formData);
    }
    
    handleClose();
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData(batch);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      const db = getDatabase();
      await remove(ref(db, `batches/${id}`));
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingBatch(null);
    setFormData({
      name: '',
      course: '',
      startDate: '',
      endDate: '',
      timing: '',
      capacity: '',
      teacher: '',
      status: 'Active',
      description: ''
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontWeight: 700, color: '#7b1fa2' }}>Batches Management</h2>
        <Button 
          variant="primary" 
          onClick={() => setShowModal(true)}
          style={{ borderRadius: '8px', padding: '8px 16px', backgroundColor: '#7b1fa2', borderColor: '#7b1fa2' }}
        >
          Add New Batch
        </Button>
      </div>

      <Row className="g-4">
        {batches.map((batch) => (
          <Col key={batch.id} lg={4} md={6}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h5 style={{ fontWeight: 600, color: '#7b1fa2' }}>{batch.name}</h5>
                  <div>
                    <Button 
                      variant="link" 
                      onClick={() => handleEdit(batch)}
                      style={{ padding: '4px' }}
                    >
                      <FaEdit color="#7b1fa2" />
                    </Button>
                    <Button 
                      variant="link" 
                      onClick={() => handleDelete(batch.id)}
                      style={{ padding: '4px' }}
                    >
                      <FaTrash color="#d32f2f" />
                    </Button>
                  </div>
                </div>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td><strong>Course:</strong></td>
                      <td>{batch.course}</td>
                    </tr>
                    <tr>
                      <td><strong>Teacher:</strong></td>
                      <td>{batch.teacher}</td>
                    </tr>
                    <tr>
                      <td><strong>Timing:</strong></td>
                      <td>{batch.timing}</td>
                    </tr>
                    <tr>
                      <td><strong>Capacity:</strong></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaUsers />
                          {batch.capacity} students
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Duration:</strong></td>
                      <td>{batch.startDate} to {batch.endDate}</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: batch.status === 'Active' ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {batch.status}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingBatch ? 'Edit Batch' : 'Add New Batch'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Batch Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Course</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Timing</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.timing}
                    onChange={(e) => setFormData({ ...formData, timing: e.target.value })}
                    placeholder="e.g. 9:00 AM - 11:00 AM"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Capacity</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Teacher</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.teacher}
                    onChange={(e) => setFormData({ ...formData, teacher: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" style={{ backgroundColor: '#7b1fa2', borderColor: '#7b1fa2' }}>
              {editingBatch ? 'Update' : 'Add'} Batch
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Batches; 